import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Honeypot } from "../components/Honeypot";
import useReveal from "../hooks/useReveal";
import { useEstimate } from "../components/EstimateContext";
import usePublicData from "../hooks/usePublicData";
import { TESTIMONIALS, COMPANY } from "../mock";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import {
  MessageSquare, LayoutGrid, Ruler, Box, Palette, FileCheck, Sofa, Sparkles,
  ShieldCheck, Eye, Layers, Plus, Minus, ArrowRight, Check, Star, Phone, CheckCircle2,
} from "lucide-react";
import { IMG, SERVICES, PROCESS, DELIVERABLES, WHY, FAQ } from "../data/designStudioData";

const ICONS = { MessageSquare, LayoutGrid, Ruler, Box, Palette, FileCheck, Sofa, Sparkles, ShieldCheck, Eye, Layers };

const Heading = ({ eyebrow, title, sub, center, dark }) => (
  <div className={`${center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"} mb-12 reveal`}>
    <div className={`flex items-center gap-3 mb-4 ${center ? "justify-center" : ""}`}>
      <span className="h-px w-10 bg-[#2f9fe6]" />
      <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">{eyebrow}</span>
      {center && <span className="h-px w-10 bg-[#2f9fe6]" />}
    </div>
    <h2 className={`font-display text-3xl sm:text-4xl font-bold leading-tight ${dark ? "text-white" : "text-[#0f2740]"}`}>{title}</h2>
    {sub && <p className={`text-lg mt-4 ${dark ? "text-[#a9c6dd]" : "text-[#6b7683]"}`}>{sub}</p>}
  </div>
);

function DesignForm() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [website, setWebsite] = useState("");
  const [form, setForm] = useState({ name: "", contact: "", rooms: "", style: "", goals: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full px-4 py-3 rounded-xl bg-white border border-white/20 text-[#0f2740] outline-none focus:ring-2 focus:ring-[#2f9fe6]/50 placeholder:text-[#9aa4af]";

  const submit = async (e) => {
    e.preventDefault();
    const message = [
      `Rooms: ${form.rooms || "—"}`,
      `Style: ${form.style || "—"}`,
      `Goals: ${form.goals || "—"}`,
    ].join("\n");
    const isEmail = form.contact.includes("@");
    try {
      await api.post("/leads", { name: form.name, email: isEmail ? form.contact : "", phone: isEmail ? "" : form.contact, service: "design-studio", message, source: "design-studio-page", website });
      setSubmitted(true);
      toast({ title: "Request received", description: "Our design team will reach out to book your consultation." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us.", variant: "destructive" });
    }
  };

  if (submitted) {
    return (
      <div className="bg-white/10 rounded-2xl p-10 text-center border border-white/15" data-testid="design-form-success">
        <CheckCircle2 className="w-14 h-14 text-[#2f9fe6] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Thank you!</h3>
        <p className="text-[#a9c6dd]">Your design consultation request has been received. Our team will be in touch shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white/5 rounded-2xl p-6 sm:p-8 border border-white/10 space-y-4" data-testid="design-form">
      <Honeypot value={website} onChange={setWebsite} />
      <div className="grid sm:grid-cols-2 gap-4">
        <input required className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" data-testid="ds-name" />
        <input required className={inputCls} value={form.contact} onChange={(e) => set("contact", e.target.value)} placeholder="Phone or email" data-testid="ds-contact" />
        <input className={inputCls} value={form.rooms} onChange={(e) => set("rooms", e.target.value)} placeholder="Rooms to design" data-testid="ds-rooms" />
        <input className={inputCls} value={form.style} onChange={(e) => set("style", e.target.value)} placeholder="Style you love (modern, warm, etc.)" data-testid="ds-style" />
      </div>
      <textarea rows={3} className={inputCls} value={form.goals} onChange={(e) => set("goals", e.target.value)} placeholder="Tell us about your project and what you'd like help designing…" data-testid="ds-goals" />
      <button type="submit" className="w-full py-3.5 rounded-full bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] transition-colors" data-testid="ds-submit">Book a Design Consultation</button>
      <a href={COMPANY.phoneHref} className="flex items-center justify-center gap-2 text-sm text-[#a9c6dd] hover:text-white transition-colors"><Phone className="w-4 h-4" /> Or call us: {COMPANY.phone}</a>
    </form>
  );
}

export default function DesignStudio() {
  useReveal();
  const { openEstimate } = useEstimate();
  const [openFaq, setOpenFaq] = useState(0);
  const { data: reviews } = usePublicData("/reviews", TESTIMONIALS);
  useReveal([reviews]);

  return (
    <>
      <Seo title="Design Studio — Interior Design in Edmonton" path="/services/design-studio"
        description="Classic Interior Renovations Design Studio: interior design in Edmonton — space planning, 3D renderings, material & furniture selection, mood boards and build-ready turnkey design packages."
        image={IMG.hero} />

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Interior design 3D rendering by Classic Interior Design Studio" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2740]/95 via-[#0f2740]/82 to-[#0f2740]/45" />
        </div>
        <div className="container-x relative z-10 pt-28 pb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#2f9fe6]" />
            <span className="text-[#bfe0f5] tracking-[0.26em] text-xs font-medium uppercase">Design Studio</span>
          </div>
          <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6 max-w-3xl">
            <span className="font-light">Design It Right,</span> <span className="font-bold text-[#2f9fe6]">Before You Build</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mb-9">
            Our in-house Design Studio turns your ideas into a clear, build-ready plan — space planning, photorealistic 3D renderings, curated materials and furnishings — so you can see and love your space before construction begins.
          </p>
          <div className="flex flex-wrap gap-4">
            <button data-testid="hero-estimate-btn" onClick={openEstimate} className="group px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold flex items-center gap-2 hover:bg-[#1f82c7] transition-all">
              Book a Consultation <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <Link to="/projects" className="px-8 py-4 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">View Our Work</Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="What We Do" title="From concept to build-ready plan" sub="Everything you need to design a space you'll love — under one roof." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <div key={s.title} className={`reveal delay-${(i % 3) + 1} group bg-[#f7f9fb] rounded-2xl overflow-hidden border border-[#eef1f4] hover:shadow-xl transition-all`} data-testid={`ds-service-${i}`}>
                  <div className="h-44 overflow-hidden"><img src={s.img} alt={s.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
                  <div className="p-7">
                    <div className="w-11 h-11 rounded-xl bg-[#eaf5fd] flex items-center justify-center mb-4 -mt-12 relative z-10 border-4 border-white"><Icon className="w-5 h-5 text-[#2f9fe6]" /></div>
                    <h3 className="font-display text-lg font-bold text-[#0f2740] mb-2">{s.title}</h3>
                    <p className="text-[#6b7683] text-sm leading-relaxed">{s.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY DESIGN FIRST */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x">
          <Heading dark eyebrow="Why Design First" title="The value of a plan before the build" sub="Designing up front saves money, time and second-guessing — and delivers a more polished result." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <div key={w.title} className={`reveal delay-${(i % 4) + 1} bg-white/5 rounded-2xl p-7 border border-white/10`}>
                  <div className="w-12 h-12 rounded-xl bg-[#2f9fe6]/15 flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                  <h3 className="font-display text-lg font-semibold mb-2">{w.title}</h3>
                  <p className="text-[#a9c6dd] text-sm leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Our Process" title="How the design studio works" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROCESS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <div key={p.title} className={`reveal delay-${(i % 3) + 1} bg-[#f7f9fb] rounded-2xl p-7 border border-[#eef1f4]`}>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-12 h-12 rounded-full bg-[#eaf5fd] flex items-center justify-center"><Icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                    <div className="text-[#2f9fe6] font-display font-bold text-sm">STEP {i + 1}</div>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-2">{p.title}</h3>
                  <p className="text-[#6b7683] text-sm">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="py-24 bg-[#f2ede4]">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <div className="shape-arch-top"><img src={IMG.samples} alt="Material and finish selections" className="w-full h-[500px] object-cover" /></div>
          </div>
          <div className="reveal-right">
            <Heading eyebrow="Deliverables" title="What you'll receive" />
            <div className="grid sm:grid-cols-2 gap-3">
              {DELIVERABLES.map((it) => (
                <div key={it} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-[#e5ddce]">
                  <span className="w-6 h-6 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span className="text-[#0f2740] text-sm font-medium">{it}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VISUALIZE */}
      <section className="py-24 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <Heading eyebrow="See It First" title="Experience your space in 3D" />
            <p className="text-[#5a6570] text-lg leading-relaxed mb-6">
              No more guessing from swatches and floor plans. Our photorealistic 3D renderings let you walk through your future kitchen, bathroom or living space — testing layouts, finishes and lighting until it feels exactly right.
            </p>
            <p className="text-[#5a6570] leading-relaxed mb-8">
              It's the confidence of knowing precisely what you're getting — and the freedom to refine every detail before construction ever begins.
            </p>
            <button onClick={openEstimate} className="inline-flex items-center gap-2 text-[#2f9fe6] font-semibold hover:gap-3 transition-all" data-testid="visualize-cta">Start your design <ArrowRight className="w-4 h-4" /></button>
          </div>
          <div className="reveal-right grid grid-cols-2 gap-4">
            <div className="shape-soft"><img src={IMG.render1} alt="3D interior rendering" className="w-full h-64 object-cover" /></div>
            <div className="shape-soft mt-8"><img src={IMG.render2} alt="3D living room visualization" className="w-full h-64 object-cover" /></div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews && reviews.length > 0 && (
        <section className="py-24 bg-[#f7f9fb]">
          <div className="container-x">
            <Heading center eyebrow="Trust" title="What our clients say" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((r, i) => (
                <div key={r.id || r.name} className={`reveal delay-${(i % 3) + 1} bg-white rounded-2xl p-7 border border-[#eef1f4]`}>
                  <div className="flex mb-3">{[...Array(r.rating || 5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-[#2f9fe6] text-[#2f9fe6]" />)}</div>
                  <p className="text-[#4a5560] text-sm leading-relaxed mb-4">“{r.text}”</p>
                  <div className="font-display font-semibold text-[#0f2740]">{r.name}</div>
                  <div className="text-sm text-[#9aa4af]">{r.role}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container-x max-w-3xl">
          <Heading center eyebrow="FAQ" title="Design studio FAQ" />
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div key={i} className="border border-[#e7e9ec] rounded-xl overflow-hidden bg-[#f7f9fb]">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left" data-testid={`ds-faq-${i}`}>
                  <span className={`font-display font-semibold ${openFaq === i ? "text-[#2f9fe6]" : "text-[#0f2740]"}`}>{f.q}</span>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${openFaq === i ? "bg-[#2f9fe6] text-white" : "bg-white text-[#0f2740]"}`}>{openFaq === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                </button>
                <div className={`overflow-hidden transition-all duration-400 ${openFaq === i ? "max-h-72" : "max-h-0"}`}><p className="px-6 pb-5 text-[#6b7683] leading-relaxed">{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + FORM */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Get Started</span></div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold mb-5">Let's design something you'll love</h2>
            <p className="text-[#a9c6dd] text-lg">Tell us about your space and your style. We'll book a consultation, explore your vision and outline a design package tailored to your Edmonton home.</p>
          </div>
          <div className="reveal-right"><DesignForm /></div>
        </div>
      </section>
    </>
  );
}
